import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Clock, Mail, ArrowRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";
import { BUSINESS, SERVICES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.lastName || !form.email || !form.message) {
      toast.error("Please complete the required fields.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: `${form.firstName} ${form.lastName}`.trim(),
        first_name: form.firstName,
        last_name: form.lastName,
        email: form.email,
        phone: form.phone,
        service: form.service,
        message: form.message,
      };
      await axios.post(`${API}/contact`, payload);
      toast.success("Thank you — we'll be in touch shortly.");
      setForm({ firstName: "", lastName: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full border-b border-black/20 bg-transparent py-3 text-base text-[#050505] placeholder:text-[#aaa] focus:border-[#050505] focus:outline-none transition-colors";

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact"
        path="/contact"
        description="Get in touch with Barrhaven Beauty Lounge at 80 Marketplace Ave, Nepean, ON. Call +1 (613) 843-1703 or send us a message to book your visit."
      />
      <PageHero
        script="contact"
        title="Let's talk beauty."
        subtitle="Have a question or ready to book? Send us a message and our team will help you plan your personalised beauty experience."
      />

      <section className="mx-auto max-w-[1400px] px-5 md:px-10 py-16 md:py-24">
        <div className="grid gap-16 lg:grid-cols-12 lg:gap-20">
          {/* Form */}
          <div className="lg:col-span-7">
            <Reveal>
              <form onSubmit={submit} className="space-y-8" data-testid="contact-form" name="contact" id="contact-form">
                <div className="grid gap-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="first_name" className="mb-2 block text-[11px] uppercase tracking-[0.16em] text-[#888]">
                      First name *
                    </label>
                    <input
                      className={inputCls}
                      id="first_name"
                      name="first_name"
                      autoComplete="given-name"
                      required
                      value={form.firstName}
                      onChange={update("firstName")}
                      placeholder="First name"
                      data-testid="contact-first-name"
                    />
                  </div>
                  <div>
                    <label htmlFor="last_name" className="mb-2 block text-[11px] uppercase tracking-[0.16em] text-[#888]">
                      Last name *
                    </label>
                    <input
                      className={inputCls}
                      id="last_name"
                      name="last_name"
                      autoComplete="family-name"
                      required
                      value={form.lastName}
                      onChange={update("lastName")}
                      placeholder="Last name"
                      data-testid="contact-last-name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="mb-2 block text-[11px] uppercase tracking-[0.16em] text-[#888]">
                      Email *
                    </label>
                    <input
                      type="email"
                      className={inputCls}
                      id="email"
                      name="email"
                      autoComplete="email"
                      required
                      value={form.email}
                      onChange={update("email")}
                      placeholder="you@email.com"
                      data-testid="contact-email"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="mb-2 block text-[11px] uppercase tracking-[0.16em] text-[#888]">
                      Phone
                    </label>
                    <input
                      className={inputCls}
                      id="phone"
                      name="phone"
                      type="tel"
                      autoComplete="tel"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="(613) 000-0000"
                      data-testid="contact-phone"
                    />
                  </div>
                  <div>
                    <label htmlFor="service" className="mb-2 block text-[11px] uppercase tracking-[0.16em] text-[#888]">
                      Service of interest
                    </label>
                    <select
                      className={`${inputCls} appearance-none`}
                      id="service"
                      name="service"
                      value={form.service}
                      onChange={update("service")}
                      data-testid="contact-service"
                    >
                      <option value="">Select a service</option>
                      {SERVICES.map((s) => (
                        <option key={s.slug} value={s.title}>
                          {s.title}
                        </option>
                      ))}
                      <option value="Other">Other / General enquiry</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label htmlFor="message" className="mb-2 block text-[11px] uppercase tracking-[0.16em] text-[#888]">
                    Message *
                  </label>
                  <textarea
                    rows={5}
                    className={`${inputCls} resize-none`}
                    id="message"
                    name="message"
                    required
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Tell us how we can help..."
                    data-testid="contact-message"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="group inline-flex items-center gap-2 bg-[#050505] px-10 py-4 text-sm uppercase tracking-[0.14em] text-white transition-colors duration-300 hover:bg-white hover:text-[#050505] border border-[#050505] disabled:opacity-60"
                >
                  {loading ? "Sending..." : "Send message"}
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </form>
            </Reveal>
          </div>

          {/* Info */}
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="space-y-8">
                <InfoRow icon={MapPin} label="Visit">
                  {BUSINESS.addressFull}
                </InfoRow>
                <InfoRow icon={Phone} label="Call">
                  <a href={BUSINESS.phoneHref} className="link-underline">
                    {BUSINESS.phoneDisplay}
                  </a>
                </InfoRow>
                <InfoRow icon={Mail} label="Email">
                  <a href={`mailto:${BUSINESS.email}`} className="link-underline">
                    {BUSINESS.email}
                  </a>
                </InfoRow>
                <InfoRow icon={Clock} label="Hours">
                  <span className="space-y-2">
                    {BUSINESS.hours.map((h) => (
                      <span key={h.day} className="flex flex-col leading-snug">
                        <span>{h.day}</span>
                        <span className="text-[#888]">{h.time}</span>
                      </span>
                    ))}
                  </span>
                </InfoRow>
              </div>

              <div className="mt-10 aspect-[4/3] w-full overflow-hidden rounded-2xl border border-black/12">
                <iframe
                  title="Barrhaven Beauty Lounge location"
                  src={BUSINESS.mapEmbed}
                  className="h-full w-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}

function InfoRow({ icon: Icon, label, children }) {
  return (
    <div className="flex gap-4 border-t border-black/12 pt-6">
      <Icon size={20} strokeWidth={1.4} className="mt-1 shrink-0 text-[#050505]" />
      <div>
        <div className="text-[11px] uppercase tracking-[0.16em] text-[#888]">{label}</div>
        <div className="mt-1 text-base leading-relaxed text-[#333]">{children}</div>
      </div>
    </div>
  );
}
